import type { ReactNode } from "react"
import { Input } from "@/components/ui/primitives"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { cn } from "@/lib/utils"

export type CatalogPickerSection = {
  id: string
  label: string
  items: ReactNode
}

export type CatalogPickerDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  closeLabel: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  searchTrailing?: ReactNode
  searchHint?: ReactNode
  sections: CatalogPickerSection[]
  emptyLabel: string
  isEmpty?: boolean
  /** Extra content between search bar and scroll body (e.g. custom filters). */
  headerExtra?: ReactNode
  /** Raise above fullscreen hosts like ShotPanel (z 100/101). */
  nested?: boolean
}

export function CatalogPickerDialog({
  open,
  onOpenChange,
  title,
  closeLabel,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchTrailing,
  searchHint,
  sections,
  emptyLabel,
  isEmpty,
  headerExtra,
  nested = false,
}: CatalogPickerDialogProps) {
  const showSearch = onSearchChange != null
  const empty = isEmpty ?? sections.length === 0

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      closeAriaLabel={closeLabel}
      nested={nested}
      maxHeightClassName="max-h-[min(90vh,48rem)]"
      contentClassName="w-[min(96vw,56rem)] text-ui-text-primary"
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      {showSearch || headerExtra ? (
        <div className="shrink-0 border-b border-ui-border px-5 py-3">
          {showSearch ? (
            <div className={cn("flex items-center gap-2", !searchTrailing && "block")}>
              <Input
                className={cn("mt-0 h-10 rounded-lg px-3 text-sm", searchTrailing ? "flex-1" : "w-full")}
                value={searchValue ?? ""}
                placeholder={searchPlaceholder}
                onChange={(e) => onSearchChange?.(e.target.value)}
              />
              {searchTrailing}
            </div>
          ) : null}
          {searchHint}
          {headerExtra}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {empty ? (
          <p className="py-8 text-center text-sm text-ui-text-muted">{emptyLabel}</p>
        ) : (
          sections.map((section) => (
            <div key={section.id} className="mb-5 last:mb-0">
              <p className="mb-2 text-[10px] font-semibold tracking-wide text-ui-text-muted uppercase">
                {section.label}
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{section.items}</div>
            </div>
          ))
        )}
      </div>
    </WorkspaceDialog>
  )
}

export function catalogPickerCardClassName(selected?: boolean) {
  return cn(
    "cursor-pointer rounded-lg border p-2 text-left transition-colors hover:bg-ui-surface-raised",
    selected ? "border-ui-accent bg-ui-accent/10" : "border-ui-border bg-ui-surface-sunken/40",
  )
}
